import { format } from "date-fns";
import { Building2, CreditCard } from "lucide-react";
import { KLARNA_MIN_CART_TOTAL_EUR } from "~/lib/klarna";
import { getDateLocaleByLocale, Language, useLanguageStore } from "~/stores/language-store";

export type PaymentMethod = 'clinic' | 'klarna';

type PaymentMethodSectionProps = {
    value: PaymentMethod;
    onChange: (value: PaymentMethod) => void;
    isWithinDateRange: boolean;
    meetsMinimumTotal: boolean;
    readOnly?: boolean;
    // Set when the appointment already has a valid Klarna authorization; shown instead of the eligibility hint
    klarnaAuthorizedUntil?: Date | null;
};

const PaymentMethodSection = ({
    value,
    onChange,
    isWithinDateRange,
    meetsMinimumTotal,
    readOnly = false,
    klarnaAuthorizedUntil,
}: PaymentMethodSectionProps) => {
    const { language } = useLanguageStore();

    const isKlarnaEligible = isWithinDateRange && meetsMinimumTotal;
    const authorizedUntilStr = klarnaAuthorizedUntil
        ? format(klarnaAuthorizedUntil, 'd. MMMM yyyy', { locale: getDateLocaleByLocale(language) })
        : null;

    const options: { id: PaymentMethod; icon: typeof Building2; titleDe: string; titleEn: string; descDe: string; descEn: string; disabled: boolean }[] = [
        {
            id: 'clinic',
            icon: Building2,
            titleDe: 'Bezahlung in der Klinik',
            titleEn: 'Pay in clinic',
            descDe: 'Zahle bequem vor Ort nach Deiner Behandlung.',
            descEn: 'Pay at the clinic during your appointment.',
            disabled: readOnly,
        },
        {
            id: 'klarna',
            icon: CreditCard,
            titleDe: 'Später zahlen mit Klarna',
            titleEn: 'Pay later with Klarna',
            descDe: authorizedUntilStr
                ? `Klarna-Zahlung autorisiert, gültig bis ${authorizedUntilStr}. Die Abbuchung erfolgt erst nach Deiner Behandlung vor Ort.`
                : isKlarnaEligible
                    ? 'Jetzt verifizieren. Die Abbuchung erfolgt erst nach Deiner Behandlung vor Ort.'
                    : !meetsMinimumTotal
                        ? `Nur für Buchungen ab ${KLARNA_MIN_CART_TOTAL_EUR} EUR verfügbar.`
                        : 'Nur für Termine innerhalb der nächsten 27 Tage verfügbar.',
            descEn: authorizedUntilStr
                ? `Klarna payment authorized, valid until ${authorizedUntilStr}. Payment is captured after your treatment.`
                : isKlarnaEligible
                    ? 'Pre-approve now, payment captured after your treatment.'
                    : !meetsMinimumTotal
                        ? `Only available for bookings of ${KLARNA_MIN_CART_TOTAL_EUR} EUR or more.`
                        : 'Only available for appointments within the next 27 days.',
            disabled: readOnly || !isKlarnaEligible,
        },
    ];

    return (
        <div className='flex flex-col gap-3'>
            <span className='text-lg font-normal text-burgundy'>
                {language === Language.GERMAN ? 'Zahlungsmethode' : 'Payment method'}
            </span>

            <div className='flex flex-col gap-3'>
                {options.map((option) => {
                    const Icon = option.icon;
                    const isSelected = value === option.id;

                    return (
                        <button
                            key={option.id}
                            type="button"
                            disabled={option.disabled}
                            onClick={() => {
                                if (!option.disabled) {
                                    onChange(option.id);
                                }
                            }}
                            className={`
                                flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all
                                ${isSelected
                                    ? `border-burgundy bg-burgundy/5 ${option.disabled ? 'cursor-default' : ''}`
                                    : option.disabled
                                        ? 'border-burgundy/15 bg-white/40 opacity-50 cursor-not-allowed'
                                        : 'border-burgundy/20 bg-white/40 hover:border-burgundy/50 cursor-pointer'
                                }
                            `}
                        >
                            <div className='flex flex-col gap-1 flex-1 min-w-0'>
                                <div className='flex items-center gap-2'>
                                    <Icon className='w-4 h-4 text-burgundy shrink-0' />
                                    <span className='text-sm font-medium text-burgundy'>
                                        {language === Language.GERMAN ? option.titleDe : option.titleEn}
                                    </span>
                                </div>
                                <span className='text-xs text-burgundy/70 font-light'>
                                    {language === Language.GERMAN ? option.descDe : option.descEn}
                                </span>
                            </div>

                            <div className={`
                                w-[18px] h-[18px] rounded-full border-[1.5px] shrink-0 flex items-center justify-center
                                ${isSelected ? 'border-burgundy' : 'border-burgundy/60'}
                            `}>
                                {isSelected && (
                                    <div className='w-2.5 h-2.5 rounded-full bg-burgundy' />
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default PaymentMethodSection;
